import type { DtoOrderDiscount, DtoSaving } from '@repo/api-types/order-retrieval';
import type { AssetDataType, PartialDraftAssetDetails, PartialQuoteAssetDetails } from '@repo/types/asset';
import { type OrderAssetDetails, isOrderAssetDetails } from '@repo/types/order';
import {
    type SalesPersonDiscount,
    type SalesPriceDiscount,
    type SalesPriceDiscountSummary,
    type SalesPriceSavings,
    isSalesPriceDiscount,
    isSalesPriceSavings,
} from '@repo/types/sales-price';
import { getCountryLanguage } from '@repo/utils';
import { isObject } from '@repo/utils/type.utils';

import { selectAssetArticles, selectAssetSummaries } from './cart.selectors';
import { selectAssetDetails } from './customer-assets.selectors';
import { extractCustomerViewType } from './customer.selectors.ts';
import { findEntityMatchingViewType } from './utils.ts';

export const selectTotalPriceIncludingTax = (data?: AssetDataType) => {
    const assetDetails = selectAssetDetails(data);

    if (!assetDetails) {
        return;
    }

    return normalizeData(assetDetails, false);
};

export const selectTotalPriceExcludingTax = (data?: AssetDataType) => {
    const assetDetails = selectAssetDetails(data);

    if (!assetDetails) {
        return;
    }

    return normalizeData(assetDetails, true);
};

export const selectPricesWithoutSalesPersonSavings = <T extends { discountType?: string | undefined }>(
    savings?: T[]
) => {
    const isSalesPersonSavings = ({ discountType }: { discountType?: string | undefined }) =>
        ['Coupons', 'Manual', 'Employee'].includes(discountType ?? '');

    return savings?.filter((saving) => !isSalesPersonSavings(saving)) ?? []; // filter out savings that are already included in sales person discounts
};

const normalizeData = (
    assetDetails: OrderAssetDetails | PartialDraftAssetDetails | PartialQuoteAssetDetails,
    excludeTax: boolean
) => {
    if (isOrderAssetDetails(assetDetails)) {
        const priceField = excludeTax ? 'netAmount' : 'grossAmount';

        const {
            Balance,
            Discount,
            GoodsValue,
            PRICE_SAVING_FAMILY: familySavingsOnItems,
            ToPay,
            Total,
        } = assetDetails.Amounts ?? {};

        // use discountedTotal if ToPay doesn't exist in Amounts
        const discountedTotal =
            Total?.[priceField] && Discount?.[priceField] ? Total[priceField] - Discount[priceField] : undefined;

        const paymentPlan = assetDetails._embedded?.paymentPlans?.[0];

        const totalValue = ToPay?.[priceField] ?? discountedTotal ?? paymentPlan?.amount;
        const itemsValue = (GoodsValue?.[priceField] ?? 0) + (familySavingsOnItems?.[priceField] ?? 0);

        return {
            totalValue,
            itemsValue,
            vat: Balance?.vatAmount ?? Total?.vatAmount ?? 0,
        };
    }

    // drafts and quotations
    const customerViewType = extractCustomerViewType(assetDetails);
    const preferredViewTypesSequence = [customerViewType, 'Employee'];

    const totalPrice = findPriceSummaryForViewTypes(assetDetails, preferredViewTypesSequence);
    const itemsValue = findOrderSubtotalForViewTypes(assetDetails, preferredViewTypesSequence);

    const priceField = excludeTax ? 'priceExclTax' : 'priceInclTax';

    return {
        totalValue: totalPrice?.inclSavings?.[priceField] ?? 0,
        itemsValue: itemsValue?.product?.exclSavings?.[priceField] ?? 0,
        vat: assetDetails.cartData?.salesPriceData?.vat?.amount ?? 0,
    };
};

const findPriceSummaryForViewTypes = (
    assetDetails: PartialDraftAssetDetails | PartialQuoteAssetDetails,
    viewTypes: string[]
) => {
    const priceSummaries = assetDetails.cartData?.salesPriceData?.orderSummary?.summary ?? [];

    return findEntityMatchingViewType(priceSummaries, viewTypes) ?? priceSummaries[0];
};

const findOrderSubtotalForViewTypes = (
    assetDetails: PartialDraftAssetDetails | PartialQuoteAssetDetails,
    viewTypes: string[]
) => {
    const subtotals = assetDetails.cartData?.salesPriceData?.orderSubtotal ?? [];

    return findEntityMatchingViewType(subtotals, viewTypes) ?? subtotals[0];
};

export const selectSalesPriceDiscounts = (data?: AssetDataType) => {
    const assetDetails = selectAssetDetails(data);

    if (!assetDetails) {
        return [];
    }

    return normalizeDiscounts(assetDetails);
};

const normalizeDiscounts = (
    assetDetails: OrderAssetDetails | PartialDraftAssetDetails | PartialQuoteAssetDetails
): SalesPersonDiscount[] => {
    if (isOrderAssetDetails(assetDetails)) {
        const customerContactDetails = assetDetails._embedded?.billingContact?.customerContactDetails;
        const employeeCompanyName =
            isObject(customerContactDetails) && typeof customerContactDetails.companyName === 'string'
                ? customerContactDetails.companyName
                : undefined;

        return (assetDetails._embedded?.orderDiscounts ?? []).map((discount) =>
            discount.discountType === 'EMPLOYEE' && employeeCompanyName
                ? { ...discount, employeeCompanyName }
                : discount
        );
    }

    const { discounts = [], savings = {} } = assetDetails.cartData?.salesPriceData ?? {};

    if (discounts.length === 0 || !isSalesPriceSavings(savings)) {
        return [];
    }

    return discounts
        .filter((discount) => isSalesPriceDiscount(discount))
        .map((discount) => {
            switch (discount.type) {
                case 'Manual': {
                    return mapManualDiscount(discount, savings);
                }
                case 'Employee': {
                    return mapEmployeeDiscount(discount, savings);
                }
                case 'Coupon': {
                    return {
                        amount: 'coupons' in savings ? (savings.coupons ?? 0) : 0,
                        discountType: 'COUPON',
                        referenceId: discount.discountDetails.value.toString(),
                    };
                }
                default: {
                    return {
                        amount: 0,
                        discountType: discount.type,
                        referenceId: discount.discountDetails.value.toString(),
                    };
                }
            }
        });
};

const mapManualDiscount = (discount: SalesPriceDiscount, savings: SalesPriceSavings): DtoOrderDiscount => {
    const discountValue = Number.parseFloat(`${discount.discountDetails.value}`);
    const amount = discount.discountDetails.type === 'PERCENTAGE_OFF' ? (savings.manual ?? 0) : discountValue;
    const percentage = discount.discountDetails.type === 'PERCENTAGE_OFF' ? discountValue : undefined;

    return {
        amount,
        discountType: 'MANUAL',
        ...(percentage && { percentage, discountCalcMethod: 'PERCENTAGE' }),
    };
};

const mapEmployeeDiscount = (discount: SalesPriceDiscount, savings: SalesPriceSavings): DtoOrderDiscount => {
    const amount = savings.employee ?? 0;
    const referenceId = `${discount.discountDetails.value}`;

    return {
        amount,
        discountType: 'EMPLOYEE',
        referenceId,
    };
};

export const selectFamilySavings = (data?: AssetDataType) => {
    const assetDetails = selectAssetDetails(data);

    if (!assetDetails) {
        return [];
    }

    const familySavings = extractSavingsForViewType('Family', assetDetails);
    const employeeSavings = extractSavingsForViewType('Employee', assetDetails);

    return familySavings.filter((familySaving) => {
        const hasMatchingEmployeeSaving = employeeSavings.some(
            ({ id, savingValue }) => id === familySaving.id && savingValue === familySaving.savingValue
        );

        return !hasMatchingEmployeeSaving;
    });
};

export const selectAssetSummarySavings = (data?: AssetDataType) => {
    const summaries = selectAssetSummaries(data);
    if (!summaries?.length) {
        return [];
    }

    const savingsList = summaries.flatMap(
        (summary) =>
            summary.discountSummary
                ?.map((discount) => ({
                    id: discount.id,
                    amount: discount.amount,
                    description: discount.texts[0]?.name,
                }))
                .filter(
                    (saving): saving is { id: string; amount: number; description: string } =>
                        Boolean(saving.id) &&
                        !Number.isNaN(saving.amount) &&
                        Boolean(saving.description) &&
                        !isSalesPersonDiscount(saving, summary.savings)
                ) ?? []
    );

    return reduceSavings(savingsList);
};

export const selectArticleLevelSavings = (data?: AssetDataType) => {
    const assetDetails = selectAssetDetails(data);

    if (!assetDetails || !isOrderAssetDetails(assetDetails)) {
        return [];
    }

    const items = selectAssetArticles(data);

    const savingsList: Array<DtoSaving & { id: string }> = items
        .flatMap((item) => {
            if (item.childItems?.length) {
                return item.childItems.flatMap((childItem) => childItem.savings ?? []);
            }

            return item.savings ?? [];
        })
        .filter((saving): saving is DtoSaving & { id: string } => Boolean(saving.id));

    return reduceSavings(selectPricesWithoutSalesPersonSavings(savingsList));
};

const extractSavingsForViewType = (viewType: string, assetDetails?: ReturnType<typeof selectAssetDetails>) => {
    if (isOrderAssetDetails(assetDetails)) {
        return [];
    }

    const summary = findEntityMatchingViewType(assetDetails?.cartData?.salesPriceData?.orderSummary?.summary ?? [], [
        viewType,
    ]);

    const savings = summary?.savings ?? {};
    const savingsSummaryList = (summary?.discountSummary ?? [])
        .filter((discount): discount is SalesPriceDiscountSummary => Boolean(discount))
        .filter((discount) => !isSalesPersonDiscount(discount, savings))
        .map((discount) => ({
            amount: discount.amount,
            description: extractDiscountSummaryName(discount),
            id: discount.id,
        }));

    return reduceSavings(savingsSummaryList);
};

const reduceSavings = (savings: Array<{ amount?: number; description?: string; id: string }>) => {
    const savingsMap = savings.reduce(
        (acc: Record<string, { id: string; description: string; savingValue: number }>, saving) => {
            const { amount = 0, description, id } = saving;

            if (id in acc) {
                return Object.assign({}, acc, {
                    [id]: { ...acc[id], savingValue: (acc[id]?.savingValue ?? 0) + amount },
                });
            }

            return Object.assign({}, acc, { [id]: { id, description, savingValue: amount } });
        },
        {}
    );

    return Object.values(savingsMap);
};

const isSalesPersonDiscount = (
    discount: { isCoupon?: boolean; id: string; amount?: number },
    savings?: { coupons?: number; employee?: number; manual?: number }
) => {
    return (
        isCouponDiscount(discount, savings) ||
        isManualDiscount(discount, savings) ||
        isEmployeeDiscount(discount, savings)
    );
};

const extractDiscountSummaryName = (discount: SalesPriceDiscountSummary) => {
    const { language } = getCountryLanguage();
    const translatedText = discount.texts.find(({ langCode }) => langCode === language.toLowerCase());

    return (translatedText ?? discount.texts[0])?.name ?? '';
};

const isCouponDiscount = (discount: { isCoupon?: boolean; amount?: number }, savings?: { coupons?: number }) => {
    return Boolean(discount.isCoupon) && discount.amount === savings?.coupons;
};

const isManualDiscount = (discount: { id: string; amount?: number }, savings?: { manual?: number }) => {
    return discount.id.toUpperCase() === 'MANUAL' && discount.amount === savings?.manual;
};

const isEmployeeDiscount = (discount: { amount?: number }, savings?: { employee?: number }) => {
    return discount.amount === savings?.employee;
};
